import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import Loading from "../components/Loading";
import { useGuestAuth } from "../context/GuestAuthContext";
import { useGetAllCarsForSale } from "../services";
import {
  currencyFormat,
  formatMileage,
  resolveListingTitle,
  resolveFirstListingImage,
  useCurrentUser,
  useCurrentUserProfile,
} from "../utils";
import { formatCityState } from "../utils/locationDisplay";
import { applyFilters, bodyStyleMatches } from "../utils/listingFilter";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import styles from "./HomePage.module.css";

const POPULAR_MAKES = [
  "Toyota",
  "Honda",
  "Ford",
  "BMW",
  "Tesla",
  "Chevrolet",
  "Mercedes-Benz",
  "Audi",
];

const GRID_PREVIEW_COUNT = 36;

type SortKey = "newest" | "priceAsc" | "priceDesc" | "mileageAsc";

type DiscoveryChip =
  | { id: string; label: string; kind: "all" }
  | { id: string; label: string; kind: "maxPrice"; maxPrice: number }
  | { id: string; label: string; kind: "maxMileage"; maxMileage: number }
  | { id: string; label: string; kind: "bodyStyle"; bodyStyle: string }
  | { id: string; label: string; kind: "make"; make: string };

type HomePageProps = {
  darkMode: boolean;
  /** Deep-link auth mode for /login and /signup routes. */
  initialAuth?: "signin" | "signup";
};

const HomePage: React.FC<HomePageProps> = ({ darkMode, initialAuth }) => {
  const navigate = useNavigate();
  const { data, error, loading } = useGetAllCarsForSale();
  const user = useCurrentUser();
  const userId = user?.id;
  const { profile } = useCurrentUserProfile();
  const { isGuest, openSignIn, openSignUp } = useGuestAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const [activeChipId, setActiveChipId] = useState("all");

  useEffect(() => {
    if (initialAuth === "signin") openSignIn();
    if (initialAuth === "signup") openSignUp();
  }, [initialAuth, openSignIn, openSignUp]);

  const allCars = useMemo(() => {
    return (
      data?.allCarsForSales?.nodes?.filter(
        (car): car is NonNullable<typeof car> =>
          car != null && (!userId || car.sellerId !== userId)
      ) || []
    );
  }, [data, userId]);

  const discoveryChips = useMemo((): DiscoveryChip[] => {
    const chips: DiscoveryChip[] = [{ id: "all", label: "All cars", kind: "all" }];
    const priceOf = (c: (typeof allCars)[number]) => {
      const n = parseFloat(c.price?.toString() ?? "");
      return Number.isFinite(n) ? n : 0;
    };

    const priceBands = [
      { id: "under15", label: "Under $15k", maxPrice: 15000 },
      { id: "under25", label: "Under $25k", maxPrice: 25000 },
      { id: "under40", label: "Under $40k", maxPrice: 40000 },
    ];
    for (const band of priceBands) {
      const count = allCars.filter((c) => priceOf(c) <= band.maxPrice).length;
      if (count > 0) {
        chips.push({
          id: band.id,
          label: band.label,
          kind: "maxPrice",
          maxPrice: band.maxPrice,
        });
      }
    }

    const lowMileageCount = allCars.filter(
      (c) => (c.mileage ?? Infinity) <= 60000
    ).length;
    if (lowMileageCount > 0) {
      chips.push({
        id: "low-miles",
        label: "Low mileage",
        kind: "maxMileage",
        maxMileage: 60000,
      });
    }

    const bodyStyles: { style: string; label: string }[] = [
      { style: "SUV", label: "SUVs" },
      { style: "Sedan", label: "Sedans" },
      { style: "Truck", label: "Trucks" },
      { style: "Coupe", label: "Coupes" },
      { style: "Hatchback", label: "Hatchbacks" },
    ];
    for (const { style, label } of bodyStyles) {
      const count = allCars.filter((c) => bodyStyleMatches(c.bodyStyle, style)).length;
      if (count >= 2) {
        chips.push({
          id: `body-${style}`,
          label,
          kind: "bodyStyle",
          bodyStyle: style,
        });
      }
    }

    for (const make of POPULAR_MAKES) {
      if (allCars.some((c) => c.make?.toLowerCase() === make.toLowerCase())) {
        chips.push({ id: `make-${make}`, label: make, kind: "make", make });
      }
    }

    return chips;
  }, [allCars]);

  // If inventory changes and the selected chip disappears, fall back to All.
  useEffect(() => {
    if (!discoveryChips.some((c) => c.id === activeChipId)) {
      setActiveChipId("all");
    }
  }, [discoveryChips, activeChipId]);

  const activeChip =
    discoveryChips.find((c) => c.id === activeChipId) ?? discoveryChips[0];

  const seeAllHref = useMemo(() => {
    if (!activeChip || activeChip.kind === "all") return "/search";
    if (activeChip.kind === "make") {
      return `/search?make=${encodeURIComponent(activeChip.make)}`;
    }
    if (activeChip.kind === "maxPrice") {
      return `/search?maxPrice=${activeChip.maxPrice}`;
    }
    if (activeChip.kind === "bodyStyle") {
      return `/search?body=${encodeURIComponent(activeChip.bodyStyle)}`;
    }
    if (activeChip.kind === "maxMileage") {
      return `/search?maxMileage=${activeChip.maxMileage}`;
    }
    return "/search";
  }, [activeChip]);

  const filteredCars = useMemo(() => {
    let cars = allCars;
    if (!activeChip || activeChip.kind === "all") {
      // no extra filter
    } else if (activeChip.kind === "maxPrice") {
      cars = applyFilters(cars, { maxPrice: activeChip.maxPrice });
    } else if (activeChip.kind === "maxMileage") {
      cars = applyFilters(cars, { maxMileage: activeChip.maxMileage });
    } else if (activeChip.kind === "bodyStyle") {
      cars = applyFilters(cars, { bodyStyle: activeChip.bodyStyle });
    } else if (activeChip.kind === "make") {
      cars = applyFilters(cars, { make: activeChip.make });
    }
    return cars;
  }, [allCars, activeChip]);

  const sortedCars = useMemo(() => {
    const arr = [...filteredCars];
    const priceOf = (c: (typeof arr)[number]) =>
      parseFloat(c.price?.toString() ?? "0");
    const dateOf = (c: (typeof arr)[number]) =>
      c.createdAt ? new Date(c.createdAt).getTime() : 0;
    switch (sortKey) {
      case "priceAsc":
        arr.sort((a, b) => priceOf(a) - priceOf(b));
        break;
      case "priceDesc":
        arr.sort((a, b) => priceOf(b) - priceOf(a));
        break;
      case "mileageAsc":
        arr.sort(
          (a, b) => (a.mileage ?? Infinity) - (b.mileage ?? Infinity)
        );
        break;
      case "newest":
      default:
        arr.sort((a, b) => dateOf(b) - dateOf(a));
        break;
    }
    return arr;
  }, [filteredCars, sortKey]);

  const gridCars = sortedCars.slice(0, GRID_PREVIEW_COUNT);
  const firstName = profile?.firstname?.trim() || null;

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  if (loading) {
    return (
      <div className={`${styles.pageContainer} ${darkMode ? styles.dark : ""}`}>
        <AppHeader />
        <Loading />
      </div>
    );
  }
  if (error) console.error("Error loading data:", error);

  return (
    <div className={`${styles.pageContainer} ${darkMode ? styles.dark : ""}`}>
      <AppHeader />

      <main className={styles.marketplace}>
        <section className={styles.discover} aria-label="Find cars">
          <div className={styles.discoverIntro}>
            {isGuest ? (
              <>
                <h1 className={styles.discoverTitle}>Find your next car</h1>
                <p className={styles.discoverSubtitle}>
                  Browse private listings, compare options, and message sellers
                  directly.
                </p>
              </>
            ) : (
              <>
                <h1 className={styles.discoverTitle}>
                  {firstName ? (
                    <>
                      Welcome back,{" "}
                      <span className={styles.discoverName}>{firstName}</span>
                    </>
                  ) : (
                    "Welcome back"
                  )}
                </h1>
                <p className={styles.discoverSubtitle}>
                  Pick up where you left off — search, filter, or browse what&apos;s
                  new.
                </p>
              </>
            )}
          </div>

          <form onSubmit={handleSearchSubmit} className={styles.searchForm}>
            <SearchIcon className={styles.searchIcon} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
              placeholder="Search by model"
              aria-label="Search listings by keywords, year, make, or model"
            />
            <button type="submit" className={styles.searchButton}>
              Search
            </button>
          </form>

          <p className={styles.searchMeta}>
            {allCars.length} active listing{allCars.length === 1 ? "" : "s"}
            {isGuest ? " · Browse free — no account needed" : null}
          </p>

          <div
            className={styles.discoveryChips}
            role="radiogroup"
            aria-label="Quick filters"
          >
            {discoveryChips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                role="radio"
                className={`${styles.discoveryChip} ${
                  activeChipId === chip.id ? styles.discoveryChipActive : ""
                }`}
                aria-checked={activeChipId === chip.id}
                onClick={() => setActiveChipId(chip.id)}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </section>

        <section className={styles.listings} aria-label="Marketplace listings">
          <div className={styles.listingsToolbar}>
            <div className={styles.listingsHeading}>
              <h2>Listings</h2>
              <p>
                {sortedCars.length} vehicle
                {sortedCars.length === 1 ? "" : "s"}
                {activeChip && activeChip.kind !== "all"
                  ? ` · ${activeChip.label}`
                  : ""}
              </p>
            </div>

            <div
              className={styles.sortTabs}
              role="radiogroup"
              aria-label="Sort listings"
            >
              {(
                [
                  ["newest", "Newest"],
                  ["priceAsc", "Lowest price"],
                  ["priceDesc", "Highest price"],
                  ["mileageAsc", "Lowest mileage"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={sortKey === key}
                  className={`${styles.sortTab} ${
                    sortKey === key ? styles.sortTabActive : ""
                  }`}
                  onClick={() => setSortKey(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {gridCars.length === 0 ? (
            <div className={styles.noResults}>
              <DirectionsCarIcon className={styles.noResultsIcon} />
              {allCars.length === 0 ? (
                <>
                  <h3>No listings yet</h3>
                  <p>Check back soon — sellers are adding cars every day.</p>
                </>
              ) : (
                <>
                  <h3>No matches</h3>
                  <p>Try another filter, or clear it to see everything.</p>
                  {activeChipId !== "all" && (
                    <button
                      type="button"
                      className={styles.clearFilterBtn}
                      onClick={() => setActiveChipId("all")}
                    >
                      Show all cars
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className={styles.grid}>
              {gridCars.map((car) => {
                const location = formatCityState(
                  (car as { locationAddress?: string }).locationAddress
                );
                return (
                  <Link
                    to={`/product/${car.id}`}
                    key={car.id}
                    className={styles.card}
                    data-testid="listing-card"
                    data-listing-id={car.id}
                  >
                    <div className={styles.cardImage}>
                      <img
                        src={resolveFirstListingImage(car.imageLoc, {
                          darkMode,
                        })}
                        alt={resolveListingTitle(car)}
                      />
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.cardPrice}>
                        {currencyFormat(
                          parseFloat(car.price?.toString() ?? "0")
                        )}
                      </div>
                      <h3 className={styles.cardTitle}>
                        {resolveListingTitle(car)}
                      </h3>
                      <div className={styles.cardMeta}>
                        <span>{formatMileage(car.mileage?.toString())}</span>
                        {location && (
                          <span className={styles.cardLocation}>
                            <LocationOnIcon aria-hidden /> {location}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {sortedCars.length > GRID_PREVIEW_COUNT && seeAllHref && (
            <div className={styles.listingsFooter}>
              <Link to={seeAllHref} className={styles.browseAllLink}>
                See all {sortedCars.length} results
                <ArrowForwardIcon aria-hidden />
              </Link>
            </div>
          )}
          {sortedCars.length > GRID_PREVIEW_COUNT && !seeAllHref && (
            <div className={styles.listingsFooter}>
              <Link to="/search" className={styles.browseAllLink}>
                Open full search
                <ArrowForwardIcon aria-hidden />
              </Link>
            </div>
          )}
        </section>

        <section className={styles.sellStrip}>
          <div className={styles.sellStripInner}>
            <div>
              <h2>Selling your car?</h2>
              <p>List for free and message buyers directly — no commissions.</p>
            </div>
            {isGuest ? (
              <button
                type="button"
                className={styles.sellStripCta}
                onClick={openSignUp}
              >
                Start selling
                <ArrowForwardIcon aria-hidden />
              </button>
            ) : (
              <Link to="/newlisting" className={styles.sellStripCta}>
                Create a free listing
                <ArrowForwardIcon aria-hidden />
              </Link>
            )}
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <h3>Drive Now!</h3>
            <p>
              The trusted marketplace for buying and selling cars directly from
              private sellers.
            </p>
          </div>
          <div className={styles.footerLinks}>
            <div className={styles.footerColumn}>
              <h4>Marketplace</h4>
              <Link to="/">Browse Cars</Link>
              <Link to="/search">Search all listings</Link>
              {isGuest ? (
                <button
                  type="button"
                  className={styles.footerLinkButton}
                  onClick={openSignUp}
                >
                  Sell Your Car
                </button>
              ) : (
                <Link to="/newlisting">Sell Your Car</Link>
              )}
              <Link to="/about">About Us</Link>
            </div>
            <div className={styles.footerColumn}>
              <h4>Support</h4>
              <Link to="/about#faq">FAQ</Link>
              <Link to="/about#how-it-works">How It Works</Link>
              <span>Safety Tips</span>
            </div>
            <div className={styles.footerColumn}>
              <h4>Legal</h4>
              <span>Terms of Service</span>
              <span>Privacy Policy</span>
            </div>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <p>© {new Date().getFullYear()} Drive Now! All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
